// storage-tab.tsx — Storage OSS business summary + analytics chart.
// GiB basis (1024³) for all storage volumes — NOT GB (1000³).
// Only rendered when service.provider.category === 'storage'.
import { useMemo, useState } from 'react';
import { Button, Card, CardBody, CardHeader, CardTitle, Skeleton } from '@vnetwork/ui/primitives';
import { EmptyState, Stat, StatGroup, TimeRangePicker } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { Chart, CHART_COLORS } from '@vnetwork/ui/charts';
import { useOpsStorageBusiness, useOpsStorageAnalytics } from '@/api/operations';
import { StorageBucketTable, formatGiB } from './storage-bucket-table';

/* ── Chart palette — Muted Material 400 ──────────────────────────────────── */

const COLOR_STORAGE = CHART_COLORS[3]; // purple — closest to indigo

/* ── Date helpers ─────────────────────────────────────────────────────────── */

function toLocalDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function daysAgo(days: number): Date {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d;
}

/* ── Props ────────────────────────────────────────────────────────────────── */

interface StorageTabProps {
  serviceId: string;
}

/* ── Component ───────────────────────────────────────────────────────────── */

/**
 * StorageTab shows OSS billing summary, a monthly padded-storage chart, and
 * a per-bucket breakdown for the selected date window.
 * GiB = 1024³ (binary) throughout — NOT GB (1000³).
 * Lazy-mounted by the parent — only rendered when tab === 'storage'.
 */
export function StorageTab({ serviceId }: StorageTabProps) {
  const [fromDate, setFromDate] = useState(() => toLocalDateStr(daysAgo(30)));
  const [toDate, setToDate] = useState(() => toLocalDateStr(new Date()));

  // RFC3339 for storage/business; YYYY-MM-DD (gte/lte) for storage/analytics.
  const fromRFC = useMemo(() => new Date(fromDate).toISOString(), [fromDate]);
  const toRFC = useMemo(() => new Date(toDate).toISOString(), [toDate]);

  const business = useOpsStorageBusiness(serviceId, { from: fromRFC, to: toRFC });
  const analytics = useOpsStorageAnalytics(serviceId, { gte: fromDate, lte: toDate });

  const chartData = useMemo(
    () => (analytics.data?.series ?? []).map((p) => ({ t: p.timestamp, storage: p.value })),
    [analytics.data],
  );

  const isLoading = business.isLoading;
  const isError = business.isError;
  const summary = business.data?.summary;
  const buckets = business.data?.buckets ?? [];

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* Date range picker */}
      <Card>
        <CardBody>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)', whiteSpace: 'nowrap' }}>
              Date range
            </span>
            <TimeRangePicker
              showLive={false}
              presets={[]}
              from={fromDate}
              to={toDate}
              triggerLabel={fromDate && toDate ? `${fromDate} → ${toDate}` : 'Select dates'}
              onApply={(v) => {
                if (v.from && v.to) {
                  setFromDate(v.from.slice(0, 10));
                  setToDate(v.to.slice(0, 10));
                }
              }}
            />
            <Button size="sm" variant="secondary" onClick={() => { setFromDate(toLocalDateStr(daysAgo(30))); setToDate(toLocalDateStr(new Date())); }}>
              Last 30 days
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Error state */}
      {isError && (
        <Alert
          tone="danger"
          title="Failed to load storage data"
          actions={
            <Button size="sm" variant="ghost" onClick={() => { business.refetch(); analytics.refetch(); }}>
              Retry
            </Button>
          }
        />
      )}

      {/* KPI summary — GiB basis (1024³) */}
      {!isError && (
        <StatGroup columns={4}>
          {isLoading ? (
            <>{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} style={{ height: 72 }} />)}</>
          ) : (
            <>
              <Stat label="Peak Storage (billed)" value={summary ? formatGiB(summary.peak_stored_bytes) : '—'} hint="GiB basis (1024³)" />
              <Stat label="Avg Storage" value={summary ? formatGiB(summary.avg_stored_bytes) : '—'} />
              <Stat label="GiB-month" value={summary ? `${(summary.gib_month ?? 0).toFixed(2)} GiB` : '—'} hint="Billed figure" />
              <Stat label="Buckets" value={String(buckets.length)} />
            </>
          )}
        </StatGroup>
      )}

      {/* Monthly storage chart */}
      {!isError && (
        <Card>
          <CardHeader><CardTitle>Storage over time (monthly)</CardTitle></CardHeader>
          <CardBody>
            {analytics.isLoading ? (
              <Skeleton style={{ height: 200 }} />
            ) : chartData.length > 0 ? (
              <Chart
                type="bar"
                height={200}
                data={chartData}
                xKey="t"
                series={[{ key: 'storage', label: 'Storage (bytes)', color: COLOR_STORAGE }]}
                showLegend
                showGrid
                valueFormatter={(v) => formatGiB(typeof v === 'number' ? v : Number(v) || 0)}
              />
            ) : (
              <EmptyState size="sm" title="No analytics data" description="No monthly storage data for the selected range." />
            )}
          </CardBody>
        </Card>
      )}

      {/* Per-bucket breakdown */}
      {!isError && <StorageBucketTable buckets={buckets} isLoading={isLoading} />}
    </div>
  );
}
